// routes.js
import React, { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { Routes, Route, Navigate, useLocation } from 'react-router-dom'
import ResetPassword from "../containers/ResetPassword/ResetPassword"
import ForgotPassword from "../containers/ForgotPassword/ForgotPassword"
import SignIn from "../containers/SignIn/SingIn"
import InternalSignIn from "../containers/InternalSignIn/InternalSignIn"
import NeutralSignIn from "../containers/SignIn/NeutralSignIn"
import NeutralInternalSignIn from "../containers/InternalSignIn/NeutralInternalSignIn"
import Customers from "../containers/Admin/Customers"
import { REDIRECT_AFTER_LOGIN, StorageGetItem, USER } from "../lib/storage"
import { useOfficeVisibility } from "../hooks/useOfficeVisibility"
import Contacts from "../containers/Contacts/Contacts"
import EditCustomerPage from "../containers/Admin/EditCustomerPage"
import ConfirmCode from "../containers/ConfirmCode/ConfirmCode"
import Contact from "../containers/Contact/Contact"
import Esign from "../containers/Esign/Esign"
import { AddContact } from "../containers/Contacts/add-contact/AddContact"
import Settings from "../containers/Settings/Settings/Settings"
import TasksPage from "../containers/Admin/Tasks/TasksPage"
import Leads from "../containers/Leads/Leads"
import EditUserPage from "../containers/Contact/EditUserPage"
import Users from "../containers/Users/Users"
import AddUsers from "../containers/Users/AddUsers"
import EditUsers from "../containers/Users/EditUsers"
import Affiliates from "../containers/Affiliates/Affiliates"
import AddAffiliates from "../containers/Affiliates/AddAffiliates"
import EditAffiliates from "../containers/Affiliates/EditAffiliates"
import { AffiliateGroupsAdmins, AddAffiliateGroupsAdmin, EditAffiliateGroupsAdmin } from "../containers/AffiliateGroupsAdmins"
import ContactGroupControl from "../containers/Settings/ContactGroups/ContactGroupControl"
import TriggerForm from "../containers/Settings/WorkflowControl/TriggerForm"
import CompanySettings from "../containers/Settings/CompanySettings/CompanySettings"
import Accounting from "../containers/Accounting/Accounting/Accounting"
import DailyFile from "../containers/Accounting/DailyFile/DailyFile"
import { useIsSupervisor } from "../hooks/user"
import HistoryPage from "../containers/History/HistoryPage"
import EditBilling from "../containers/BankAccount/BillingInfoComponents/EditBiiling"
import EditTransaction from "../components/Transactions/EditTransaction"
import ManageApiKey from "../containers/Settings/ContactGroups/ManageApiKey"
import AdminResetPassword from "../containers/AdminResetPassword/AdminResetPassword"
import SalesDashboard from "../containers/SalesDashboard/SalesDashboard"
import PortalDashboard from "../containers/Portal/PortalDashboard"
import PortalActivity from "../containers/Portal/PortalActivity"
import AccountSelector from '../containers/Portal/AccountSelector'
import OfficeSelector from '../containers/Office/OfficeSelector'
import { MyTeam, AddTeamMember, TeamRoute } from '../containers/MyTeam'
import CompanySelector from '../containers/Company/CompanySelector'
import { SELECT_COMPANY_PATH } from '../lib/http'
import PaymentProcessing from "../containers/Payments/PaymentProcessing"
import { isPublicRoute } from "../lib/publicRoutes"
import { selectFeatureFlagsSettled } from '../features/featureFlags/selectors'
import { AFFILIATE_TASKS_UI } from '../config/featureFlagKeys'
import usePinnedFeatureFlag from '../hooks/usePinnedFeatureFlag'

const AffiliateTasksRoute = () => {
  const { enabled } = usePinnedFeatureFlag(AFFILIATE_TASKS_UI)
  const flagsSettled = useSelector(selectFeatureFlagsSettled)

  if (!flagsSettled) return null

  return enabled ? <TasksPage/> : <Navigate to="/" replace />
}

const RedirectToLogin = () => {
  const location = useLocation()
  const intendedPath = location.pathname + location.search
  const params = new URLSearchParams(location.search)
  const isTeamPortal = params.get('portal') === 'team'

  if (isPublicRoute(location.pathname)) {
    return null
  }

  const isAuthFlowPath =
    intendedPath === '/' ||
    intendedPath === '/forgot-password' ||
    intendedPath === '/forgot-password/internal' ||
    intendedPath === '/confirm-code' ||
    intendedPath === '/confirm-code/internal' ||
    intendedPath === '/reset-password' ||
    intendedPath === '/reset-password/internal' ||
    intendedPath === '/select-office' ||
    intendedPath === '/select-company'
  if (!isAuthFlowPath) {
    localStorage.setItem(REDIRECT_AFTER_LOGIN, intendedPath)
  }

  return <Navigate to={isTeamPortal ? '/team-portal' : '/'} replace />
}

export const AuthRoutes = ({ setForgotPassword, neutral = false }) => {
  return (
    <Routes>
      <Route path="/" element={neutral ? <NeutralSignIn/> : <SignIn setForgotPassword={setForgotPassword}/>}/>
      <Route path="/team-portal" element={neutral ? <NeutralInternalSignIn/> : <InternalSignIn />}/>
      <Route path="/forgot-password" element={<ForgotPassword neutral={neutral}/>}/>
      <Route path="/forgot-password/internal" element={<ForgotPassword neutral={neutral}/>}/>
      <Route path="/confirm-code" element={<ConfirmCode neutral={neutral}/>}/>
      <Route path="/confirm-code/internal" element={<ConfirmCode neutral={neutral}/>}/>
      <Route path="/reset-password" element={<ResetPassword neutral={neutral}/>}/>
      <Route path="/reset-password/internal" element={<ResetPassword neutral={neutral}/>}/>
      <Route path="*" element={<RedirectToLogin />}/>
    </Routes>
  )
}

export const AppRoutes = () => {
  const [user, setUser] = useState(null)
  const [loading, setLoading] = useState(true)
  const role = user?.role
  const isSupervisor = useIsSupervisor()
  const { level: officeVisibilityLevel, resolved: officeVisibilityResolved } = useOfficeVisibility(user)
  const dashboardHidden = officeVisibilityResolved && officeVisibilityLevel === 'HIDDEN'
  const homeElement = dashboardHidden ? <Navigate to="/leads" replace /> : <SalesDashboard/>

  useEffect(() => {
    StorageGetItem(USER)
      .then(result => setUser(result))
      .finally(() => setLoading(false))
  }, [])

  if (loading) return null

  // Multi-brand. The brand picker is role-independent and has to exist for EVERY authenticated role,
  // because the response interceptor sends any of them here with a hard window.location assignment
  // on a company refusal. Registered inside one role's <Routes> it was a redirect loop for everyone
  // else: the hard navigation lands on a path that role's router does not know, the catch-all sends
  // them home, and the next request 403s straight back here.
  //
  // Handled before the switch rather than repeated in eight route tables, so it cannot be forgotten
  // in one of them.
  if (window.location.pathname === SELECT_COMPANY_PATH) {
    return <CompanySelector/>
  }

  switch (role) {
    case 'admin':
      return (
        <Routes>
          <Route path="/" element={<Customers/>}/>
          <Route path="/customers" element={<Customers/>}/>
          <Route path="/customer/edit/:customerId" element={<EditCustomerPage/>}/>
          <Route path="/payments/daily-processing" element={<PaymentProcessing initialView="toProcess"/>}/>
          <Route path="/payments/processed-payments" element={<PaymentProcessing initialView="processed"/>}/>
          <Route path="*" element={<Navigate to="/" replace />}/>
        </Routes>
      )
    case 'customer':
      return (
        <Routes>
          <Route path="/" element={homeElement}/>
          <Route path="/leads" element={<Leads/>}/>
          <Route path="/customers" element={<Contacts/>}/>
          <Route path="/esign" element={<Esign/>}/>
          <Route path="/contact/:contactId" element={<Contact/>}/>
          <Route path="/contact/add-contact" element={<AddContact/>}/>
          <Route path="/lead/edit/:userId" element={<AddContact/>}/>
          <Route path="/contact/edit/:userId" element={<EditUserPage successNavTo="/customers"/>}/>
          <Route path="/settings/*" element={<Settings/>}/>
          <Route path="/accounting" element={<Accounting/>}/>
          <Route path="/accounting/daily-files/:fileId" element={<DailyFile/>}/>
          <Route path="/payments/daily-processing" element={<PaymentProcessing initialView="toProcess"/>}/>
          <Route path="/payments/processed-payments" element={<PaymentProcessing initialView="processed"/>}/>
          <Route path="/tasks" element={<TasksPage/>}/>
          <Route path="/users" element={<Users/>}/>
          <Route path="/users/add-user" element={<AddUsers/>}/>
          <Route path="/users/edit/:userId" element={<EditUsers/>}/>
          <Route path="/affiliates" element={<Affiliates/>}/>
          <Route path="/affiliates/add" element={<AddAffiliates/>}/>
          <Route path="/affiliates/edit/:userId" element={<EditAffiliates/>}/>
          <Route path="/affiliate-groups-admins" element={<AffiliateGroupsAdmins/>}/>
          <Route path="/affiliate-groups-admins/add" element={<AddAffiliateGroupsAdmin/>}/>
          <Route path="/affiliate-groups-admins/edit/:userId" element={<EditAffiliateGroupsAdmin/>}/>
          <Route path="/contact-groups/add" element={<ContactGroupControl/>}/>
          <Route path="/contact-groups/edit/:groupId" element={<ContactGroupControl isEdit={true}/>}/>
          <Route path="/triggers/add" element={<TriggerForm mode="create"/>}/>
          <Route path="/triggers/edit/:id" element={<TriggerForm mode="edit"/>}/>
          <Route path="/company-settings" element={<CompanySettings/>}/>
          <Route path="/contact/view/:userId" element={<EditUserPage viewOnly={true}/>}/>
          <Route path="/history" element={<HistoryPage/>}/>
          <Route path="/billing/:billingId/view/:billingType" element={<EditBilling viewOnly={true}/>}/>
          <Route path="/billing/:billingId/edit/:billingType" element={<EditBilling viewOnly={false}/>}/>
          <Route path="/transactions/:transactionId/edit/:contactId" element={<EditTransaction/>}/>
          <Route path="/contact-groups/:groupId/api-key" element={<ManageApiKey/>}/>
          <Route path="/admin/reset-password" element={<AdminResetPassword/>}/>
          <Route path="*" element={<Navigate to="/" replace />}/>
        </Routes>
      )
    case 'user':
      if (isSupervisor) {
        return (
          <Routes>
            <Route path="/" element={homeElement}/>
            <Route path="/leads" element={<Leads/>}/>
            <Route path="/customers" element={<Contacts/>}/>
            <Route path="/esign" element={<Esign/>}/>
            <Route path="/contact/:contactId" element={<Contact/>}/>
            <Route path="/contact/add-contact" element={<AddContact/>}/>
            <Route path="/lead/edit/:userId" element={<AddContact/>}/>
            <Route path="/contact/edit/:userId" element={<EditUserPage successNavTo="/customers"/>}/>
            <Route path="/accounting" element={<Accounting/>}/>
            <Route path="/accounting/daily-files/:fileId" element={<DailyFile/>}/>
            <Route path="/history" element={<HistoryPage/>}/>
            <Route path="/tasks" element={<TasksPage/>}/>
            <Route path="/contact/view/:userId" element={<EditUserPage viewOnly={true}/>}/>
            <Route path="/billing/:billingId/view/:billingType" element={<EditBilling viewOnly={true}/>}/>
            <Route path="/billing/:billingId/edit/:billingType" element={<EditBilling viewOnly={false}/>}/>
            <Route path="*" element={<Navigate to="/" replace />}/>
          </Routes>
        )
      } else {
        return (
          <Routes>
            <Route path="/" element={homeElement}/>
            <Route path="/leads" element={<Leads/>}/>
            <Route path="/history" element={<HistoryPage/>}/>
            <Route path="/customers" element={<Contacts/>}/>
            <Route path="/contact/:contactId" element={<Contact/>}/>
            <Route path="/contact/add-contact" element={<AddContact/>}/>
            <Route path="/tasks" element={<TasksPage/>}/>
            <Route path="/lead/edit/:userId" element={<AddContact/>}/>
            <Route path="/contact/view/:userId" element={<EditUserPage viewOnly={true}/>}/>
            <Route path="/contact/edit/:userId" element={<EditUserPage successNavTo="/customers"/>}/>
            <Route path="*" element={<Navigate to="/" replace />}/>
          </Routes>)
      }
    case 'affiliate':
      // No client-side gate on "does this affiliate need to pick an office". The stored
      // user only knows its memberships, while an office can also come from ownership, so
      // any guess here is wrong in one direction or the other — and guessing "yes" for an
      // affiliate whose only groups are departments loops: the picker resolves an empty
      // list, sends them home, and the gate sends them straight back. The server decides:
      // the first request answers OfficeSelectionRequired and the interceptor routes here.
      return (
        <Routes>
          <Route path="/" element={homeElement}/>
          <Route path="/select-office" element={<OfficeSelector/>}/>
          <Route path="/leads" element={<Leads/>}/>
          <Route path="/contact/add-contact" element={<AddContact/>}/>
          <Route path="/lead/edit/:userId" element={<AddContact/>}/>
          <Route path="/customers" element={<Contacts/>}/>
          <Route path="/contact/:contactId" element={<Contact/>}/>
          <Route path="/contact/view/:userId" element={<EditUserPage viewOnly={true}/>}/>
          <Route path="/tasks" element={<AffiliateTasksRoute/>}/>
          {/* accessLevel 'fullContactsNoAccounting' is an explicit denial and must beat group
              ownership — otherwise deep-linking /accounting still renders the page for the five
              affiliates found on 2026-08-05. The server enforces the same rule. */}
          {user?.isGroupOwner && user?.accessLevel !== 'fullContactsNoAccounting' && (
            <Route path="/accounting" element={<Accounting/>}/>
          )}
          {user?.isGroupOwner && user?.accessLevel !== 'fullContactsNoAccounting' && (
            <Route path="/accounting/daily-files/:fileId" element={<DailyFile/>}/>
          )}
          <Route path="/my-team" element={<TeamRoute user={user}><MyTeam/></TeamRoute>}/>
          <Route path="/my-team/add" element={<TeamRoute user={user}><AddTeamMember/></TeamRoute>}/>
          <Route path="*" element={<Navigate to="/" replace />}/>
        </Routes>
      )
    case 'affiliateGroupsAdmin':
      return (
        <Routes>
          <Route path="/" element={homeElement}/>
          <Route path="/leads" element={<Leads/>}/>
          <Route path="/contact/add-contact" element={<AddContact/>}/>
          <Route path="/lead/edit/:userId" element={<AddContact/>}/>
          <Route path="/customers" element={<Contacts/>}/>
          <Route path="/contact/:contactId" element={<Contact/>}/>
          <Route path="/contact/view/:userId" element={<EditUserPage viewOnly={true}/>}/>
          <Route path="/tasks" element={<AffiliateTasksRoute/>}/>
          <Route path="/accounting" element={<Accounting/>}/>
          <Route path="/accounting/daily-files/:fileId" element={<DailyFile/>}/>
          <Route path="/contact/edit/:userId" element={<EditUserPage successNavTo="/customers"/>}/>
          <Route path="/my-team" element={<TeamRoute user={user}><MyTeam/></TeamRoute>}/>
          <Route path="/my-team/add" element={<TeamRoute user={user}><AddTeamMember/></TeamRoute>}/>
          <Route path="*" element={<Navigate to="/" replace />}/>
        </Routes>
      )
    case 'contact':
      return (
        <Routes>
          <Route path="/" element={<PortalDashboard />} />
          <Route path="/portal" element={<PortalDashboard />} />
          <Route path="/portal/activity" element={<PortalActivity />} />
          <Route path="/portal/*" element={<PortalDashboard />} />
          <Route path="*" element={<Navigate to="/" replace />}/>
        </Routes>
      )
    case 'co-applicant':
      return (
        <Routes>
          <Route path="/" element={<AccountSelector />} />
          <Route path="/portal" element={<PortalDashboard />} />
          <Route path="/portal/activity" element={<PortalActivity />} />
          <Route path="/portal/*" element={<PortalDashboard />} />
          <Route path="/select-account" element={<AccountSelector />} />
          <Route path="*" element={<Navigate to="/" replace />}/>
        </Routes>
      )
    default:
      return (
        <Routes>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      )
  }

}

